import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import {
  ArrowTopRightOnSquareIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { Link } from '@leadsales/design-system/react';
import { VITE_ONBOARDING_LIVE_SESSION_URL } from 'src/constants/envs';
import { useBusinessHours } from 'src/hooks/whatsapp/useBusinessHours.hook';
import { useAdvisorScheduleUrl } from 'src/hooks/whatsapp/useAdvisorScheduleUrl.hook';
import type { tConnectionFailedAdvisorCtaKind } from 'src/components/WhatsAppConnect/ConnectionFailedError/ConnectionFailedError.types';
import {
  WIZARD_BODY_CLASS_NAME,
  WIZARD_TRANSLATION_NAMESPACE,
} from './WhatsAppConnectionWizard.constants';
import type { tWizardExternalLinkVariant } from './WhatsAppConnectionWizard.types';

export type tWhatsAppConnectionWizardExternalLinkProps = {
  href: string;
  label: string;
  variant: tWizardExternalLinkVariant;
  onClick?: () => void;
  /** Layout classes of the link's own placement. */
  className?: string;
};

export type tWhatsAppConnectionWizardAdvisorLinkProps = {
  variant: tWizardExternalLinkVariant;
  onAdvisorCtaClick: (kind: tConnectionFailedAdvisorCtaKind) => void;
  className?: string;
};

export type tWhatsAppConnectionWizardAdvisorRowProps = {
  onAdvisorCtaClick: (kind: tConnectionFailedAdvisorCtaKind) => void;
};

/**
 * A wizard link that opens in a new tab and says so to screen readers. The
 * quiet one is a DS text link; the others are links styled as DS buttons,
 * since the DS button renders only a `button`.
 *
 * @param {tWhatsAppConnectionWizardExternalLinkProps} props - Where it goes,
 * its label and weight.
 * @returns {JSX.Element} The link.
 */
export default function WhatsAppConnectionWizardExternalLink(
  props: tWhatsAppConnectionWizardExternalLinkProps,
) {
  const { href, label, variant, onClick, className } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const newTabText = (
    <span className="sr-only">{` ${t('wizard.actions.advisorNewTab')}`}</span>
  );

  if (variant === 'quiet') {
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        icon={<ArrowTopRightOnSquareIcon aria-hidden="true" />}
        className={className}
      >
        {label}
        {newTabText}
      </Link>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={classNames('ls-btn', `ls-btn-${variant}`, className)}
    >
      {label}
      <ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
      {newTabText}
    </a>
  );
}

/**
 * "Hablar con asesor": the live session while an advisor is on, the schedule
 * page otherwise, reporting which one the person opened.
 *
 * @param {tWhatsAppConnectionWizardAdvisorLinkProps} props - Its weight and
 * the click report.
 * @returns {JSX.Element} The advisor link.
 */
export function WhatsAppConnectionWizardAdvisorLink(
  props: tWhatsAppConnectionWizardAdvisorLinkProps,
) {
  const { variant, onAdvisorCtaClick, className } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const { isLiveSessionAvailable } = useBusinessHours();
  const scheduleUrl = useAdvisorScheduleUrl();
  const kind: tConnectionFailedAdvisorCtaKind = isLiveSessionAvailable
    ? 'live'
    : 'schedule';

  return (
    <WhatsAppConnectionWizardExternalLink
      href={
        isLiveSessionAvailable ? VITE_ONBOARDING_LIVE_SESSION_URL : scheduleUrl
      }
      label={t('wizard.actions.advisor')}
      variant={variant}
      onClick={() => onAdvisorCtaClick(kind)}
      className={className}
    />
  );
}

/**
 * "O conecta con un asesor" with its button, under the answers of step 1 and
 * at the end of an error screen whose footer does not lead with the advisor.
 * While an advisor is on, the button opens the live session; otherwise it
 * schedules one.
 *
 * @param {tWhatsAppConnectionWizardAdvisorRowProps} props - The click report.
 * @returns {JSX.Element} The advisor row.
 */
export function WhatsAppConnectionWizardAdvisorRow(
  props: tWhatsAppConnectionWizardAdvisorRowProps,
) {
  const { onAdvisorCtaClick } = props;
  const { t } = useTranslation(WIZARD_TRANSLATION_NAMESPACE);
  const { isLiveSessionAvailable } = useBusinessHours();
  const scheduleUrl = useAdvisorScheduleUrl();
  const kind: tConnectionFailedAdvisorCtaKind = isLiveSessionAvailable
    ? 'live'
    : 'schedule';

  return (
    <div className="grid grid-cols-[auto_1fr] items-center gap-[var(--ls-spacing-2)] md:flex">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--ls-radius-md)] bg-[var(--ls-surface-background)]">
        <CalendarDaysIcon
          className="h-4 w-4 text-[var(--ls-text-neutral-light)]"
          aria-hidden="true"
        />
      </span>
      <span className={classNames(WIZARD_BODY_CLASS_NAME, 'md:flex-1')}>
        {t('wizard.advisorRow.lead')}
      </span>
      <WhatsAppConnectionWizardExternalLink
        href={
          isLiveSessionAvailable
            ? VITE_ONBOARDING_LIVE_SESSION_URL
            : scheduleUrl
        }
        label={t(
          isLiveSessionAvailable
            ? 'wizard.actions.advisor'
            : 'wizard.advisorRow.schedule',
        )}
        variant="tertiary"
        onClick={() => onAdvisorCtaClick(kind)}
        className="ls-btn-sm col-span-2 justify-self-start"
      />
    </div>
  );
}
